import { store } from '../store'
import {
  setMFAState,
  startMFAEnrollment,
  completeMFAEnrollment,
  cancelMFAEnrollment,
  removeMFACredential,
} from '../store/slices/authSlice'
import type { MFACredential, MFAState } from '../store/slices/authSlice'
import { getToken } from './auth'

// API Base URLs
const API_BASE_URL = import.meta.env['VITE_API_URL'] || 'http://localhost:8080'
const MFA_API_URL = `${API_BASE_URL}/api/v1/mfa`

// MFA enrollment request/response types
export interface TOTPEnrollmentRequest {
  label?: string
}

export interface TOTPEnrollmentResponse {
  credentialId: string
  qrCode: string
  secret: string
  backupCodes: string[]
}

export interface SMSEnrollmentRequest {
  phoneNumber: string
  label?: string
}

export interface SMSEnrollmentResponse {
  credentialId: string
  maskedPhoneNumber: string
}

export interface MFAVerificationRequest {
  credentialId: string
  code: string
}

export interface MFAVerificationResponse {
  success: boolean
  backupCodes?: string[]
}

export interface MFACredentialListResponse {
  credentials: MFACredential[]
  availableMethods: ('totp' | 'sms')[]
  isRequired: boolean
}

// API Helper function
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken()
  if (!token) {
    throw new Error('No authentication token available')
  }

  const response = await fetch(`${MFA_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`API request failed: ${response.status} - ${error}`)
  }

  return response.json()
}

/**
 * Load user's MFA configuration and credentials
 */
export const loadMFAState = async (): Promise<void> => {
  try {
    const mfaData = await apiRequest<MFACredentialListResponse>('/credentials')

    const mfaState: MFAState = {
      isRequired: mfaData.isRequired,
      isConfigured: mfaData.credentials.length > 0,
      availableMethods: mfaData.availableMethods,
      configuredMethods: mfaData.credentials,
      enrollmentInProgress: false,
    }

    store.dispatch(setMFAState(mfaState))
  } catch (error) {
    console.error('Failed to load MFA state:', error)
    throw error
  }
}

/**
 * Start TOTP enrollment process
 */
export const startTOTPEnrollment = async (
  label?: string
): Promise<TOTPEnrollmentResponse> => {
  try {
    const request: TOTPEnrollmentRequest = { label }
    const response = await apiRequest<TOTPEnrollmentResponse>('/totp/enroll', {
      method: 'POST',
      body: JSON.stringify(request),
    })

    store.dispatch(
      startMFAEnrollment({
        method: 'totp',
        qrCodeData: response.qrCode,
      })
    )

    return response
  } catch (error) {
    console.error('Failed to start TOTP enrollment:', error)
    throw error
  }
}

/**
 * Complete TOTP enrollment with verification code
 */
export const completeTOTPEnrollment = async (
  credentialId: string,
  code: string
): Promise<void> => {
  try {
    const request: MFAVerificationRequest = { credentialId, code }
    const response = await apiRequest<MFAVerificationResponse>('/totp/verify', {
      method: 'POST',
      body: JSON.stringify(request),
    })

    if (response.success) {
      const credential: MFACredential = {
        id: credentialId,
        type: 'totp',
        label: 'Authenticator App',
        createdAt: new Date().toISOString(),
      }

      store.dispatch(
        completeMFAEnrollment({
          credential,
          backupCodes: response.backupCodes,
        })
      )
    } else {
      throw new Error('TOTP verification failed')
    }
  } catch (error) {
    console.error('Failed to complete TOTP enrollment:', error)
    throw error
  }
}

/**
 * Start SMS enrollment process
 */
export const startSMSEnrollment = async (
  phoneNumber: string,
  label?: string
): Promise<SMSEnrollmentResponse> => {
  try {
    const request: SMSEnrollmentRequest = { phoneNumber, label }
    const response = await apiRequest<SMSEnrollmentResponse>('/sms/enroll', {
      method: 'POST',
      body: JSON.stringify(request),
    })

    store.dispatch(
      startMFAEnrollment({
        method: 'sms',
      })
    )

    return response
  } catch (error) {
    console.error('Failed to start SMS enrollment:', error)
    throw error
  }
}

/**
 * Complete SMS enrollment with verification code
 */
export const completeSMSEnrollment = async (
  credentialId: string,
  code: string
): Promise<void> => {
  try {
    const request: MFAVerificationRequest = { credentialId, code }
    const response = await apiRequest<MFAVerificationResponse>('/sms/verify', {
      method: 'POST',
      body: JSON.stringify(request),
    })

    if (response.success) {
      const credential: MFACredential = {
        id: credentialId,
        type: 'sms',
        label: 'SMS Authentication',
        createdAt: new Date().toISOString(),
      }

      store.dispatch(
        completeMFAEnrollment({
          credential,
          backupCodes: response.backupCodes,
        })
      )
    } else {
      throw new Error('SMS verification failed')
    }
  } catch (error) {
    console.error('Failed to complete SMS enrollment:', error)
    throw error
  }
}

/**
 * Cancel MFA enrollment process
 */
export const cancelEnrollment = async (): Promise<void> => {
  store.dispatch(cancelMFAEnrollment())
}

/**
 * Remove MFA credential
 */
export const removeMFAMethod = async (credentialId: string): Promise<void> => {
  try {
    await apiRequest(`/credentials/${credentialId}`, {
      method: 'DELETE',
    })

    store.dispatch(removeMFACredential(credentialId))
  } catch (error) {
    console.error('Failed to remove MFA credential:', error)
    throw error
  }
}

/**
 * Verify MFA code during authentication
 */
export const verifyMFACode = async (
  credentialId: string,
  code: string
): Promise<boolean> => {
  try {
    const request: MFAVerificationRequest = { credentialId, code }
    const response = await apiRequest<MFAVerificationResponse>('/verify', {
      method: 'POST',
      body: JSON.stringify(request),
    })

    return response.success
  } catch (error) {
    console.error('Failed to verify MFA code:', error)
    return false
  }
}

/**
 * Send SMS verification code
 */
export const sendSMSCode = async (credentialId: string): Promise<void> => {
  try {
    await apiRequest(`/sms/${credentialId}/send`, {
      method: 'POST',
    })
  } catch (error) {
    console.error('Failed to send SMS code:', error)
    throw error
  }
}

/**
 * Generate new backup codes
 */
export const generateBackupCodes = async (): Promise<string[]> => {
  try {
    const response = await apiRequest<{ backupCodes: string[] }>(
      '/backup-codes',
      {
        method: 'POST',
      }
    )
    return response.backupCodes
  } catch (error) {
    console.error('Failed to generate backup codes:', error)
    throw error
  }
}

// Export MFA service object
export const mfaService = {
  loadMFAState,
  startTOTPEnrollment,
  completeTOTPEnrollment,
  startSMSEnrollment,
  completeSMSEnrollment,
  cancelEnrollment,
  removeMFAMethod,
  verifyMFACode,
  sendSMSCode,
  generateBackupCodes,
}
